interface Config {
  baseUrl: string;
  globalSCVApiHost: string;
  oauthConfig: OAuthConfig;
  oauthConfigExternal?: OAuthConfig;
  testApiUrl?: string;
  defaultCountryCode?: string;
  featureToggles: {};
  cloudfrontAuthConfig?: CloudfrontAuthConfig;
}

export interface OAuthConfig {
  baseUrl: string;
  issuer: string;
  redirectUri: string;
  clientId: string;
  pkce: boolean;
  scopes?: string[];
  cookies?: {
    secure: boolean;
  };
}

interface CloudfrontAuthConfig {
  PKCE_CODE_VERIFIER_LENGTH: number;
  SESSION_DURATION: number;
  DISCOVERY_DOCUMENT: string;
  AUTH_REQUEST: {
    response_type: string;
    scope: string;
  };
  TOKEN_REQUEST: {
    grant_type: string;
  };
  privateKeySsm: string;
  publicKeySsm: string;
  loginUri: string;
}

export const getConfigurationByEnv = (envName: string | undefined): Config => {
  if (!envName) {
    envName = '';
  }
  let cfg: Config;
  let baseUrl: string;
  let externalBaseUrl: string;
  switch (envName) {
    case 'develop':
      baseUrl = 'https://dev-global-scv-web.retailsolutions.dev';
      externalBaseUrl = 'https://dev-global-scv-web-external.retailsolutions.dev';
      cfg = {
        baseUrl,
        globalSCVApiHost: 'https://api.dev.globalscv.fcl.cloud/',
        oauthConfig: {
          baseUrl: 'https://flightcentre.oktapreview.com',
          issuer: 'https://flightcentre.oktapreview.com/oauth2/default',
          redirectUri: baseUrl + '/implicit/callback',
          clientId: '0oatg0wwyvyhRiAfZ0h7',
          pkce: true,
          scopes: ['openid', 'profile'],
        },
        oauthConfigExternal: {
          baseUrl: 'https://agent-login.oktapreview.com',
          issuer: 'https://agent-login.oktapreview.com/oauth2/default',
          redirectUri: externalBaseUrl + '/implicit/callback',
          clientId: '0oa1bmq1liSxPe5rA1d7',
          pkce: true,
          scopes: ['openid', 'profile'],
        },
        defaultCountryCode: 'AU',
        featureToggles: {},
      };
      break;
    case 'qa':
      baseUrl = 'https://web.qa.globalscv.fcl.cloud';
      externalBaseUrl = 'https://web-external.qa.globalscv.fcl.cloud';
      cfg = {
        baseUrl,
        globalSCVApiHost: 'https://api.qa.globalscv.fcl.cloud/',
        oauthConfig: {
          baseUrl: 'https://fcttg-test.okta.com',
          issuer: 'https://fcttg-test.okta.com/oauth2/aus1pe0lvrlIa2yzq0h8',
          redirectUri: baseUrl + '/implicit/callback',
          clientId: '0oa1pe0i8k6RBn9kh0h8',
          pkce: true,
          scopes: ['openid', 'profile'],
        },
        oauthConfigExternal: {
          baseUrl: 'https://agent-login.okta.com',
          issuer: 'https://agent-login.okta.com/oauth2/default',
          redirectUri: externalBaseUrl + '/implicit/callback',
          clientId: '0oa541f8batbynaQL4x7',
          pkce: true,
          scopes: ['openid', 'profile'],
        },
        defaultCountryCode: 'AU',
        featureToggles: {},
      };
      break;
    case 'emea-stage':
      baseUrl = 'https://web.stg.globalscv.fcl.cloud';
      externalBaseUrl = 'https://web-external.stg.globalscv.fcl.cloud';
      cfg = {
        baseUrl,
        globalSCVApiHost: 'https://api.stg.globalscv.fcl.cloud/',
        oauthConfig: {
          baseUrl: 'https://fcttg-test.okta.com',
          issuer: 'https://fcttg-test.okta.com/oauth2/aus1pe0lvrlIa2yzq0h8',
          redirectUri: baseUrl + '/implicit/callback',
          clientId: '0oa1rdqds8133t0w40h8',
          pkce: true,
          scopes: ['openid', 'profile'],
        },
        oauthConfigExternal: {
          baseUrl: 'https://agent-login.okta.com',
          issuer: 'https://agent-login.okta.com/oauth2/default',
          redirectUri: externalBaseUrl + '/implicit/callback',
          clientId: '0oa4uo01m6epgLuCN4x7',
          pkce: true,
          scopes: ['openid', 'profile'],
        },
        defaultCountryCode: 'AU',
        featureToggles: {},
      };
      break;
    case 'emea-prod':
      baseUrl = 'https://web.prd.globalscv.fcl.cloud';
      externalBaseUrl = 'https://web-external.prd.globalscv.fcl.cloud';
      cfg = {
        baseUrl,
        globalSCVApiHost: 'https://api.prd.globalscv.fcl.cloud/',
        oauthConfig: {
          baseUrl: 'https://fctg.okta.com',
          issuer: 'https://fctg.okta.com/oauth2/default',
          redirectUri: baseUrl + '/implicit/callback',
          clientId: '0oa8e67asytjtwpdE2p7',
          pkce: true,
          scopes: ['openid', 'profile'],
        },
        oauthConfigExternal: {
          baseUrl: 'https://agent-login.okta.com',
          issuer: 'https://agent-login.okta.com/oauth2/default',
          redirectUri: externalBaseUrl + '/implicit/callback',
          clientId: '0oa4uo0xkwQ4cvh9E4x7',
          pkce: true,
          scopes: ['openid', 'profile'],
        },
        defaultCountryCode: 'AU',
        featureToggles: {},
      };
      break;
    default:
      //local dev
      baseUrl = 'http://localhost:3000';
      externalBaseUrl = 'http://localhost-external:3000';
      cfg = {
        baseUrl,
        globalSCVApiHost: 'http://localhost:5150/',
        oauthConfig: {
          baseUrl: 'https://flightcentre.oktapreview.com',
          issuer: 'https://flightcentre.oktapreview.com/oauth2/default',
          redirectUri: baseUrl + '/implicit/callback',
          clientId: '0oatf6m15qTn39gZ30h7',
          pkce: false, // need this setting for localhost
          cookies: {
            secure: false, // need this setting for localhost
          },
          scopes: ['openid', 'profile'],
        },
        oauthConfigExternal: {
          baseUrl: 'https://agent-login.oktapreview.com',
          issuer: 'https://agent-login.oktapreview.com/oauth2/default',
          redirectUri: externalBaseUrl + '/implicit/callback',
          clientId: '0oa1bms4i4pIc00eg1d7',
          pkce: false, // need this setting for localhost
          cookies: {
            secure: false, // need this setting for localhost
          },
          scopes: ['openid', 'profile'],
        },
        defaultCountryCode: 'AU',
        featureToggles: {},
      };
  }
  if (cfg) {
    getFixedConfiguration(cfg);
  }

  return cfg;
};

const getFixedConfiguration = (cfg: Config) => {
  if (!cfg) {
    return;
  }

  cfg.testApiUrl = 'customerProfile?customerProfileId=1&countryCode=stub';
  cfg.cloudfrontAuthConfig = {
    PKCE_CODE_VERIFIER_LENGTH: 43,
    SESSION_DURATION: 3600,
    DISCOVERY_DOCUMENT: '/.well-known/openid-configuration',
    AUTH_REQUEST: {
      response_type: 'code',
      scope: 'openid profile',
    },
    TOKEN_REQUEST: {
      grant_type: 'authorization_code',
    },
    privateKeySsm: '/retail/serverless/environment/global-scv-web/cloudfront/privateKey',
    publicKeySsm: '/retail/serverless/environment/global-scv-web/cloudfront/publicKey',
    loginUri: cfg.baseUrl + '/login',
  };
};

const envName = process.env.REACT_APP_STAGE ? process.env.REACT_APP_STAGE : '';
const config = getConfigurationByEnv(envName);

export function getOauthConfig(external?: boolean): OAuthConfig {
  if (external && config.oauthConfigExternal) {
    return config.oauthConfigExternal;
  }

  return config.oauthConfig;
}

export default config;
